<template>
  <div class="w-100">
    <b-row align-v="center" class="ml-0 mb-3 text-center">
      <b-col cols="5" class="p-0">
        <b-input-group>
          <b-input-group-prepend>
            <b-input-group-text class="bg-white pr-1" style="border-right: none;">
              <b-icon-search font-scale="0.9" scale="1.1" />
            </b-input-group-text>
          </b-input-group-prepend>
          <b-form-input v-model="filter" placeholder="Filter by keyword..." type="search" debounce="500" style="border-left: none;" v-test="'filter'"></b-form-input>
        </b-input-group>
      </b-col>
      <b-col v-if="type === CheckboxGroupType.Pools" cols="5" class="ml-auto">
        <b-checkbox v-model="preferences.excludeFixedNumbers" @change="toggleExcludeFixedNumbers" switch>Exclude Fixed Numbers From Pools</b-checkbox>
      </b-col>
    </b-row>
    <div class="w-100 bg-light p-3 rounded-lg bordered checkbox-group">
      <div v-if="isLoading" class="d-flex flex-column align-items-center">
        <b-spinner />
      </div>
      <b-form-group v-else class="mb-0">
        <b-form-checkbox
          v-if="options.length > 0"
          v-model="allSelected"
          :indeterminate="indeterminate"
          @change="toggleAll"
          class="mb-3"
        >
          Select All Current {{ type }}
        </b-form-checkbox>
        <b-form-checkbox-group
          v-model="preferences.selected"
          class="ml-4"
          stacked
          v-test="'checkbox group'"
        >
          <b-form-checkbox
            v-for="option in options"
            :value="option.value"
            :key="option.text"
            class="mb-3"
          >
            {{ option.text }}
          </b-form-checkbox>
        </b-form-checkbox-group>
        <b-form-text v-if="options.length === 0" class="h6">No {{ type }} were found.</b-form-text>
        <b-form-row v-else-if="options.length > 0 && options.length !== allValues.length && filter.length < 3" class="ml-4">
          <b-button @click="loadAdditionalOptions" size="sm" class="pt-0 pb-0" variant="outline-secondary"><b-spinner v-if="isLoadingMore" small /> <b-icon-three-dots v-else /></b-button>
        </b-form-row>
      </b-form-group>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { BIconSearch, BIconThreeDots, FormInputPlugin, InputGroupPlugin } from 'bootstrap-vue'
import { PreferencesConfig } from '@/views/Preferences.vue'

Vue.use(FormInputPlugin)
Vue.use(InputGroupPlugin)

export enum CheckboxGroupType {
  Pools = 'Tracking Pools',
  FixedNumbers = 'Fixed Numbers'
}

@Component({
  components: {
    BIconSearch,
    BIconThreeDots
  }
})
export default class CheckboxGroup extends Vue {
  @Prop({ required: true })
  type!: CheckboxGroupType

  @Prop({ required: true, type: Array })
  options!: { text: string; value: number }[]

  @Prop({ required: true, type: Object })
  preferences!: PreferencesConfig

  @Prop({ required: true, type: Array })
  allValues!: number[]

  readonly CheckboxGroupType = CheckboxGroupType

  private isLoading = false
  private isLoadingMore = false

  private allSelected = false
  private indeterminate = false
  private filter = ''

  created () {
    this.allSelected = this.preferences.selected.length === this.allValues.length
    this.indeterminate = !this.allSelected && this.preferences.selected.length > 0
  }

  toggleAll (checked: boolean) {
    Vue.set(this.preferences, 'selected', checked ? this.allValues : [])
  }

  toggleExcludeFixedNumbers (checked: boolean) {
    Vue.set(this.preferences, 'excludeFixedNumbers', checked)
  }

  async loadAdditionalOptions () {
    this.isLoadingMore = true

    if (this.type === CheckboxGroupType.Pools) {
      await this.$store.dispatch('getTrackingPools', { filter: this.filter, append: true, offset: this.options.length })
    } else {
      await this.$store.dispatch('getFixedNumbers', { filter: this.filter, append: true, offset: this.options.length })
    }

    this.isLoadingMore = false
  }

  @Watch('preferences.selected')
  updateSelected (newValue: number[]) {
    // Handle changes in individual checkboxes
    if (newValue.length === 0) {
      this.indeterminate = false
      this.allSelected = false
    } else if (newValue.length === this.allValues.length) {
      this.indeterminate = false
      this.allSelected = true
    } else {
      this.indeterminate = true
      this.allSelected = false
    }
  }

  @Watch('filter')
  async updateFilter (value: string) {
    if (value.length >= 3 || value === '') {
      this.isLoading = true

      if (this.type === CheckboxGroupType.Pools) {
        await this.$store.dispatch('getTrackingPools', { filter: this.filter })
      } else {
        await this.$store.dispatch('getFixedNumbers', { filter: this.filter })
      }

      this.isLoading = false
    }
  }
}
</script>

<style lang="scss">
.checkbox-group {
  max-height: 20rem;
  overflow: scroll;
}
</style>
